import { Injectable, OnDestroy, inject } from '@angular/core';
import { CallAuditTrackingService } from './call-audit-tracking.service';
import { CallAuditEventType, TrackEventItem } from '../models/call-audit-activity.model';

/**
 * Measures a QA reviewer's **active** handling time on a single audit and buffers
 * page-level user events for that review.
 *
 * "Active" = the audit detail is open AND the tab is visible AND the window is
 * focused AND the user isn't idle. Time only accrues while active; it pauses on
 * tab-hidden / blur / idle. Deltas are flushed on a heartbeat, on lifecycle
 * transitions, and (via keepalive beacon) on page unload.
 *
 * Provided at the component level so its lifetime — and `ngOnDestroy` teardown —
 * is tied to the call-quality component.
 */
@Injectable()
export class AuditActivityTracker implements OnDestroy {
  private tracking = inject(CallAuditTrackingService);

  private readonly HEARTBEAT_MS = 15_000;
  private readonly IDLE_MS = 60_000;

  // Session state
  private sessionActive = false;
  private sessionId = '';
  private auditId: string | null = null;
  private botId: string | null = null;
  private productName: string | null = null;

  // Active-time accounting
  private isActive = false;
  private activeSince: number | null = null;
  private unflushedMs = 0;
  private lastInteractionAt = 0;

  private heartbeatTimer: any = null;
  private eventBuffer: TrackEventItem[] = [];

  constructor() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('blur', this.onWindowBlur);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  // ---- Public API -----------------------------------------------------------

  get currentSessionId(): string {
    return this.sessionId;
  }

  /** Begin timing a review. Ends any prior session first (flushing it). */
  startSession(auditId: string | null | undefined, botId?: string | null, productName?: string | null): void {
    if (!auditId) return;
    if (this.sessionActive) this.endSession();

    this.auditId = String(auditId);
    this.botId = botId ?? null;
    this.productName = productName ?? null;

    this.sessionId = this.uuid();

    this.sessionActive = true;
    this.unflushedMs = 0;
    this.isActive = false;
    this.activeSince = null;
    this.lastInteractionAt = Date.now();

    this.addActivityListeners();
    this.recomputeActive();
    this.startHeartbeat();

    this.track('AUDIT_OPENED');
    this.flushEvents();
    // deltaMs 0 but opened:true → increments open_count and sets first_opened_at
    this.flush({ opened: true, force: true });
  }

  /** Buffer a page-level user event for the current session. */
  track(eventType: CallAuditEventType, metadata?: Record<string, any> | null): void {
    if (!this.sessionActive || !this.auditId) return;
    this.markInteraction();
    this.eventBuffer.push({
      auditId: this.auditId,
      botId: this.botId || undefined,
      sessionId: this.sessionId,
      eventType,
      metadata: metadata || undefined,
      productName: this.productName || undefined,
      occurredAt: new Date().toISOString(),
    } as any);
  }

  /** Record verification: flush time with verified:true, then end the session. */
  trackVerified(): void {
    if (!this.sessionActive) return;
    // this.track('AUDIT_VERIFIED'); // Handled by backend synchronously
    this.endSession(true); // End session gracefully without a "Hard Close" event
  }

  /** End the review: capture the final active slice and flush everything. */
  endSession(skipClosedEvent = false): void {
    if (!this.sessionActive) return;

    if (!skipClosedEvent) {
      this.track('AUDIT_CLOSED');
    }

    this.recomputeActive();
    this.accrue();
    // Pass force: true, but also preserve verified:true if it was a verification flush
    this.flush({ force: true, verified: skipClosedEvent ? true : undefined });
    this.flushEvents();
    this.stopHeartbeat();
    this.removeActivityListeners();
    this.setActive(false);

    this.sessionActive = false;

    this.auditId = null;
    this.botId = null;
    this.productName = null;
    this.sessionId = '';
    this.unflushedMs = 0;
    this.activeSince = null;
  }

  ngOnDestroy(): void {
    if (this.sessionActive) this.endSession();
    this.stopHeartbeat();
    this.removeActivityListeners();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('blur', this.onWindowBlur);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  // ---- Active-time accounting ----------------------------------------------

  private accrue(): void {
    if (this.isActive && this.activeSince != null) {
      const now = Date.now();
      this.unflushedMs += now - this.activeSince;
      this.activeSince = now;
    }
  }

  private setActive(active: boolean): void {
    if (active === this.isActive) return;
    this.accrue(); // bank time under the previous state before switching
    this.isActive = active;
    this.activeSince = active ? Date.now() : null;
  }

  private recomputeActive(): void {
    const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    const visible = document.visibilityState === 'visible';
    const notIdle = Date.now() - this.lastInteractionAt < this.IDLE_MS;
    this.setActive(this.sessionActive && focused && visible && notIdle);
  }

  private markInteraction(): void {
    const wasIdle = !this.isActive;
    this.lastInteractionAt = Date.now();
    if (wasIdle) this.recomputeActive();
  }

  // ---- Flushing -------------------------------------------------------------

  private flush(opts?: { opened?: boolean; verified?: boolean; force?: boolean; beacon?: boolean }): void {
    if (!this.auditId) return;
    this.accrue();
    const delta = Math.round(this.unflushedMs);
    const opened = !!opts?.opened;
    const verified = !!opts?.verified;

    if (delta <= 0 && !opened && !verified && !opts?.force) return;

    const payload = {
      auditId: this.auditId,
      botId: this.botId,
      sessionId: this.sessionId,
      deltaMs: delta,
      opened,
      verified,
      productName: this.productName,
    };

    if (opts?.beacon) {
      this.tracking.reviewTimeBeacon(payload);
    } else {
      this.tracking.reviewTime(payload).subscribe({ error: () => void 0 });
    }
    this.unflushedMs = 0;
  }

  private flushEvents(beacon = false): void {
    if (this.eventBuffer.length === 0) return;
    const batch = this.eventBuffer.splice(0, this.eventBuffer.length);
    if (beacon) {
      this.tracking.trackEventsBeacon(batch);
    } else {
      this.tracking.trackEvents(batch).subscribe({
        error: (err) => console.error('[AuditActivityTracker] Flush failed:', err)
      });
    }
  }

  // ---- Heartbeat ------------------------------------------------------------

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.onHeartbeat(), this.HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private onHeartbeat(): void {
    this.recomputeActive();
    this.flush();
    this.flushEvents();
  }

  // ---- Listeners ------------------------------------------------------------

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.recomputeActive(); // pauses accrual (not visible)
      this.flush();
      this.flushEvents();
    } else {
      this.recomputeActive();
    }
  };

  private onWindowFocus = (): void => this.recomputeActive();

  private onWindowBlur = (): void => {
    this.recomputeActive();
    this.flush();
    this.flushEvents();
  };

  private onPageHide = (): void => {
    this.recomputeActive();
    this.flush({ beacon: true });
    this.flushEvents(true);
  };

  private onBeforeUnload = (): void => {
    this.recomputeActive();
    this.flush({ beacon: true });
    this.flushEvents(true);
  };

  private onUserActivity = (): void => {
    const wasIdle = !this.isActive;
    this.lastInteractionAt = Date.now();
    if (wasIdle) this.recomputeActive();
  };

  private addActivityListeners(): void {
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener('mousemove', this.onUserActivity, opts);
    window.addEventListener('mousedown', this.onUserActivity, opts);
    window.addEventListener('keydown', this.onUserActivity, opts);
    window.addEventListener('scroll', this.onUserActivity, opts);
    window.addEventListener('touchstart', this.onUserActivity, opts);
  }

  private removeActivityListeners(): void {
    window.removeEventListener('mousemove', this.onUserActivity);
    window.removeEventListener('mousedown', this.onUserActivity);
    window.removeEventListener('keydown', this.onUserActivity);
    window.removeEventListener('scroll', this.onUserActivity);
    window.removeEventListener('touchstart', this.onUserActivity);
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
